// frontend/src/components/booking/PaymentForm.jsx
import React, { useState } from 'react';

const PaymentForm = ({ bookingData, flight, onSubmit, onBack }) => {
  const [paymentData, setPaymentData] = useState({
    payment_method: 'credit_card',
    card_number: '',
    card_holder: '',
    expiry_date: '',
    cvv: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!paymentData.card_holder.trim()) {
      newErrors.card_holder = 'Cardholder name is required';
    }

    if (!paymentData.card_number.trim()) {
      newErrors.card_number = 'Card number is required';
    } else if (paymentData.card_number.replace(/\s/g, '').length < 16) {
      newErrors.card_number = 'Invalid card number';
    }

    if (!paymentData.expiry_date) {
      newErrors.expiry_date = 'Expiry date is required';
    }

    if (!paymentData.cvv.trim()) {
      newErrors.cvv = 'CVV is required';
    } else if (paymentData.cvv.length < 3) {
      newErrors.cvv = 'Invalid CVV';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    // Simulate payment processing delay
    setTimeout(() => {
      onSubmit(paymentData);
      setLoading(false);
    }, 2000);
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const totalAmount = flight.base_price * bookingData.passengers.length;

  return (
    <div className="space-y-6">
      {/* Booking Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Booking Summary</h2>
        
        <div className="space-y-3 mb-4">
          <div className="flex justify-between">
            <span>Flight:</span>
            <span className="font-medium">{flight.airline_name} {flight.flight_number}</span>
          </div>
          <div className="flex justify-between">
            <span>Route:</span>
            <span>{flight.origin_airport} → {flight.destination_airport}</span>
          </div>
          <div className="flex justify-between">
            <span>Passengers:</span>
            <span>{bookingData.passengers.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Price per person:</span>
            <span>${flight.base_price}</span>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-3">
          <div className="flex justify-between items-center text-lg font-semibold">
            <span>Total Amount:</span>
            <span className="text-blue-600">${totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Payment Information</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <select
              value={paymentData.payment_method}
              onChange={(e) => handleChange('payment_method', e.target.value)}
              className="input"
            >
              <option value="credit_card">Credit Card</option>
              <option value="debit_card">Debit Card</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cardholder Name *
            </label>
            <input
              type="text"
              value={paymentData.card_holder}
              onChange={(e) => handleChange('card_holder', e.target.value)}
              className={`input ${errors.card_holder ? 'error-input' : ''}`}
              placeholder="John Doe"
            />
            {errors.card_holder && (
              <p className="text-red-500 text-sm mt-1">{errors.card_holder}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Card Number *
            </label>
            <input
              type="text"
              value={paymentData.card_number}
              onChange={(e) => handleChange('card_number', formatCardNumber(e.target.value))}
              className={`input ${errors.card_number ? 'error-input' : ''}`}
              placeholder="1234 5678 9012 3456"
              maxLength="19"
            />
            {errors.card_number && (
              <p className="text-red-500 text-sm mt-1">{errors.card_number}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date *
              </label>
              <input
                type="month"
                value={paymentData.expiry_date}
                onChange={(e) => handleChange('expiry_date', e.target.value)}
                className={`input ${errors.expiry_date ? 'error-input' : ''}`}
                min={new Date().toISOString().slice(0, 7)}
              />
              {errors.expiry_date && (
                <p className="text-red-500 text-sm mt-1">{errors.expiry_date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CVV *
              </label>
              <input
                type="text"
                value={paymentData.cvv}
                onChange={(e) => handleChange('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
                className={`input ${errors.cvv ? 'error-input' : ''}`}
                placeholder="123"
                maxLength="4"
              />
              {errors.cvv && (
                <p className="text-red-500 text-sm mt-1">{errors.cvv}</p>
              )}
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  Your payment information is secure and encrypted. This is a demo application with mock payment processing.
                </p>
              </div>
            </div>
          </div>

          <div className="flex space-x-4 pt-6">
            <button
              type="button"
              onClick={onBack}
              className="flex-1 btn btn-secondary py-3"
              disabled={loading}
            >
              Back
            </button>
            <button
              type="submit"
              className="flex-1 btn btn-primary py-3"
              disabled={loading}
            >
              {loading ? 'Processing...' : `Pay $${totalAmount.toFixed(2)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;






